import { rem } from 'polished'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import * as Analytics from 'analytics/analytics'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import SolidTimesIcon from 'LuxKit/Icons/solid/SolidTimesIcon'
import ItemMenuOptions from 'tripPlanner/components/ItemCard/Common/ItemMenuOptions'
import DeleteTripItem from 'tripPlanner/components/TripModal/TripItems/DeleteTripItem'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import MenuButton from 'tripPlanner/Tripkit/Core/MenuButton/MenuButton'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { offerIdOrPlaceId } from 'tripPlanner/utils/analytics'
import { NarrowCardOnlyBookedLabel, WideCardOnlyBookedLabel } from './BookedLabel'
import { NarrowCardOnlySuggestedLabel, WideCardOnlySuggestedLabel } from './SuggestedLabel'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import useDesktopWithoutTouch from 'tripPlanner/hooks/useDesktopWithoutTouch'
import { DetailCardAndPinLayout, TransportCardLayout } from './DetailCardAndPinLayout'
import { CompactCardAndPinLayout } from 'tripPlanner/components/CompactItemCard/common'

const FloatRight = styled.div`
  float: right;
  padding-left: ${rem(8)};

  @media print {
    display: none;
  }
`

const HoverOnlyAssistChip = styled(AssistChip)`
  display: none;
  @container (min-width: 640px) {
    &.showOnlyOnHover {
      ${DetailCardAndPinLayout.CardContainer}:hover &,
      ${DetailCardAndPinLayout.CardContainer}.focus &,
      ${CompactCardAndPinLayout.CardContainer}:hover &,
      ${CompactCardAndPinLayout.CardContainer}.focus &,
      ${TransportCardLayout}:hover & {
        display: inline-flex;
      }
    }
  }
`

interface Props {
  item: TripItem
  offerLink?: string
  onRequestAddComment?: () => void
}

function CornerControls({
  item,
  offerLink,
  onRequestAddComment,
}: Props) {
  const intl = useIntl()
  const openModal = useOpenModal()
  const canEdit = useCanEditTrip()
  const isDesktopWithoutTouch = useDesktopWithoutTouch()

  const onClickDelete = useCallback(
    () => {
      const onDeleted = (item: TripItem) => {
        const analyticsId = offerIdOrPlaceId(item)
        if (analyticsId) {
          Analytics.trackClientEvent({
            subject: `remove_recommended_item_${analyticsId}`,
            action: 'concierge',
            type: 'interaction',
            category: AnalyticsPage.tripPlanner,
          })
        }
      }
      openModal(DeleteTripItem, { item, onDeleted })
    },
    [openModal, item],
  )

  const isBooked = 'isBooked' in item && item.isBooked

  return (
    <FloatRight>
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {item.isSuggested && <>
          <WideCardOnlySuggestedLabel />
          <NarrowCardOnlySuggestedLabel />
          <Clickable onClick={onClickDelete} data-testid="delete-from-trip-button">
            <SolidTimesIcon colour="neutral-three" />
          </Clickable>
        </>}
        {!item.isSuggested && <>
          {isBooked && <WideCardOnlyBookedLabel />}
          {isBooked && <NarrowCardOnlyBookedLabel />}
          {canEdit && (<>
            {!isBooked && isDesktopWithoutTouch && (
              <HoverOnlyAssistChip
                className="showOnlyOnHover"
                size="small"
                onClick={onClickDelete}
                variant="outline"
                startIcon={<LineTrashAltIcon size="XS" />}
                aria-label={intl.formatMessage({
                  id: 'CVL-Z19pqYbLOvvEMai-',
                  defaultMessage: 'Delete from trip',
                  description: 'Accessible label for button to delete an item from the trip plan',
                })}
                data-testid="delete-from-trip-button"
              />
            )}
            <MenuButton
              title=""
              bodyContent={<ItemMenuOptions item={item} offerLink={offerLink} onRequestAddComment={onRequestAddComment} />}
              modalOnMobile
            />
          </>)}
        </>}
      </Group>
    </FloatRight>
  )
}

export default CornerControls
